const TokenGenerator = require('./TokenGenerator');

const STORE_DOMAIN = 'arbitrip.com';

// Prefers Riskified's own beacon-generated session id (window.RISKX.getSessionId(),
// once the beacon is integrated — a separate step) so the id we send as cart_token
// matches what the beacon itself is reporting. Falls back to a freshly-generated id
// (via TokenGenerator.generateSessionId(), the existing uuid-v4 convention already used
// for search_token) with no persistence, only when the beacon isn't available at the
// moment of the call. The fallback is deliberately NOT persisted (e.g. via a cookie):
// it only ever fires when the beacon never loaded for this visit at all, in which case
// there's no beacon-observed behavioral data to correlate with regardless of which
// fallback value is used — so persisting it across calls buys nothing.
function getCartToken() {
    if (typeof window !== 'undefined' && window.RISKX && typeof window.RISKX.getSessionId === 'function') {
        try {
            const beaconSessionId = window.RISKX.getSessionId();
            if (beaconSessionId) {
                return beaconSessionId;
            }
        } catch (e) {
            // Riskified's beacon is third-party code we don't control — a failure here
            // must never block checkout. Fall through to the generated fallback below.
        }
    }
    return TokenGenerator.generateSessionId();
}

// Injects Riskified's beacon script once (Standard Integration — auto-generated session,
// no &sid= param). Adapted from Riskified's documented snippet with one deliberate change:
// the docs' example always does `window.addEventListener('load', riskifiedBeaconLoad)`,
// assuming it's an inline <script> in the initial HTML (so the page's 'load' event hasn't
// fired yet). We call this from a React mount effect instead, where the 'load' event may
// have *already* fired by the time this runs — in which case a 'load' listener would
// silently never fire and the beacon would never load. Guarded by checking
// document.readyState first.
function riskifiedBeaconLoad() {
    try {
        var url = 'https://beacon.riskified.com?shop=' + STORE_DOMAIN;
        var s = document.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = url;
        var x = document.getElementsByTagName('script')[0];
        if (x && x.parentNode) {
            x.parentNode.insertBefore(s, x);
        } else {
            document.head.appendChild(s);
        }
    } catch (e) {
        // Never let injection failures affect the app.
    }
}

let beaconLoadRequested = false;

function loadBeacon() {
    if (beaconLoadRequested) {
        return;
    }
    beaconLoadRequested = true;
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }
    try {
        if (document.readyState === 'complete') {
            riskifiedBeaconLoad();
        } else {
            window.addEventListener('load', riskifiedBeaconLoad, false);
        }
    } catch (e) {
        // Never let injection failures affect the app.
    }
}

// Tells the already-loaded beacon about an SPA route change, so Riskified sees each
// client-side navigation as a distinct pageview (no full page reload to trigger one
// naturally). Must never throw — window.RISKX is third-party code we don't control, and
// this runs inside a React effect on every navigation, so an uncaught error here is not a
// quiet failure — it can affect app rendering, not just fraud-signal quality.
function trackPageview(pathname) {
    if (typeof window === 'undefined' || !window.RISKX || typeof window.RISKX.go !== 'function') {
        return;
    }
    try {
        window.RISKX.go(pathname);
    } catch (e) {
        // Never let a beacon-side failure break navigation/rendering.
    }
}

module.exports = {
    getCartToken,
    loadBeacon,
    trackPageview,
};
